import { useState } from "react";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import i18next from "i18next";
import { isNotEmpty, findIndexBy, noop } from "neetocist";
import { Search } from "neetoicons";
import { Checkbox, Dropdown, Input, Label, Button } from "neetoui";
import PropTypes from "prop-types";
import { assoc, isEmpty, pluck, includes } from "ramda";
import { useTranslation } from "react-i18next";

import "./columns.scss";
import Item from "./Item";
import useTableConfigurations from "./useTableConfigurations.js";
import { filterBySearchTerm, getHidableColumns } from "./utils.js";

const Columns = ({
  actionBlock,
  checkboxProps = {},
  columnData = [],
  isSearchable = false,
  tableKey = "",
  noColumnMessage = i18next.t("neetoMolecules.columns.noColumns"),
  searchProps = {},
  allowConfigurationResetOnUpdate = true,
  onMutationSuccess = noop,
  defaultHiddenColumns = [],
  ...dropdownProps
}) => {
  const { Divider, Menu, MenuItem } = Dropdown;
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();

  const {
    allProcessedColumns,
    existingColumnConfiguration,
    hideColumn,
    showColumn,
    updateColumns,
    destroyConfiguration,
    isTableConfigurationFetching,
  } = useTableConfigurations(
    tableKey,
    columnData,
    onMutationSuccess,
    allowConfigurationResetOnUpdate,
    defaultHiddenColumns
  );

  const hiddenColumns = pluck(
    "key",
    existingColumnConfiguration.filter(column => column.isShown === false)
  );

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = findIndexBy(
      { key: active.id },
      existingColumnConfiguration
    );

    const newIndex = findIndexBy({ key: over.id }, existingColumnConfiguration);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = [...existingColumnConfiguration];
    const [movedItem] = newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, movedItem);
    updateColumns(newOrder);
  };

  const columns = getHidableColumns(allProcessedColumns);
  const filteredColumns = filterBySearchTerm(searchTerm, columns);

  const handleHideAllColumns = () => {
    const columnsToHideKeys = pluck("key", columns);

    updateColumns(
      existingColumnConfiguration.map(column =>
        includes(column.key, columnsToHideKeys)
          ? assoc("isShown", false, column)
          : column
      )
    );
  };

  const handleChange = ({ target: { name: dataIndex, checked } }) => {
    if (checked) showColumn(dataIndex);
    else hideColumn(dataIndex);
  };

  const handleSearch = ({ target: { value } }) => setSearchTerm(value);

  return (
    <Dropdown
      appendTo={document.body}
      buttonSize="medium"
      buttonStyle="text"
      closeOnSelect={false}
      position="bottom-end"
      onClose={() => setSearchTerm("")}
      {...dropdownProps}
    >
      <Menu>
        <div
          className="neeto-molecules-columns__container"
          data-testid="customize-columns-dropdown-container"
        >
          {isSearchable && (
            <Input
              className="px-5 py-2.5"
              data-testid="neeto-ui-columns-search"
              placeholder={t("neetoMolecules.columns.search")}
              prefix={<Search />}
              type="search"
              value={searchTerm}
              onChange={handleSearch}
              {...searchProps}
            />
          )}
          {isEmpty(filteredColumns) && (
            <span className="neeto-ui-flex neeto-ui-flex-col neeto-ui-items-center neeto-ui-p-2">
              {noColumnMessage}
            </span>
          )}
          {isNotEmpty(filteredColumns) && !searchTerm && (
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={pluck("key", filteredColumns)}
                strategy={verticalListSortingStrategy}
              >
                {filteredColumns.map(column => (
                  <Item
                    {...{
                      checkboxProps,
                      column,
                      hiddenColumns,
                      isTableConfigurationFetching,
                    }}
                    key={column.key}
                    onToggle={handleChange}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
          {isNotEmpty(filteredColumns) &&
            searchTerm &&
            filteredColumns.map(({ key, title }) => (
              <MenuItem key={key}>
                <Label
                  className="neeto-ui-flex neeto-ui-w-full hover:neeto-ui-bg-gray-200 neeto-ui-cursor-pointer px-5 py-2"
                  htmlFor={key}
                >
                  <Checkbox
                    checked={!hiddenColumns.includes(key)}
                    data-testid="neeto-ui-columns-checkbox"
                    id={key}
                    label={title}
                    name={key}
                    onChange={handleChange}
                    {...checkboxProps}
                  />
                </Label>
              </MenuItem>
            ))}
          <Divider />
          {!!actionBlock && (
            <>
              {actionBlock}
              <Divider />
            </>
          )}
          <div className="flex justify-end gap-2 px-5 py-2">
            <Button
              data-testid="column-visibility-hide-all-button"
              label={t("neetoMolecules.columns.hideAll")}
              size="small"
              style="tertiary"
              onClick={handleHideAllColumns}
            />
            <Button
              data-testid="column-visibility-reset-button"
              label={t("neetoMolecules.columns.reset")}
              size="small"
              style="secondary"
              onClick={() => destroyConfiguration(tableKey)}
            />
          </div>
        </div>
      </Menu>
    </Dropdown>
  );
};

Columns.propTypes = {
  /**
   * Controls whether the table configuration should be reset on adding custom fields (optional).
   */
  allowConfigurationResetOnUpdate: PropTypes.bool,
  /**
   * Extra action items that can be added at the end of the dropdown (optional).
   */
  actionBlock: PropTypes.element,
  /**
   * Additional props for the checkboxes in the dropdown (optional).
   */
  checkboxProps: PropTypes.object,
  /**
   * The list of columns from which the dropdown options are built (required).
   */
  columnData: PropTypes.array,
  /**
   * Controls whether the dropdown options should be searchable (optional).
   */
  isSearchable: PropTypes.bool,
  /**
   * The table key used to identify table configuration in the API (required).
   */
  tableKey: PropTypes.string,
  /**
   * The message to be shown when there are no columns to be displayed in the dropdown (optional).
   */
  noColumnMessage: PropTypes.string,
  /**
   * Props to be passed to the search input in the column dropdown. Only works when `isSearchable` is set to `true` (optional).
   */
  searchProps: PropTypes.object,
  /**
   * The function will be called when the mutation is successful (optional).
   */
  onMutationSuccess: PropTypes.func,
};

Columns.displayName = "Columns";
export default Columns;
